import React, { useEffect, useRef, useState } from 'react';

import styled from '@emotion/styled';

import { FontWeight } from '@clutter/clean';

import { useStorageCheckoutContext } from '@root/components/checkout/context';
import { useTrack } from '@root/initializers/wt';

import { DisposalPricingModal } from '../disposal_pricing_modal';
import { CardPadded, InlineBrandLink, SelectRow, SelectStack } from '../shared';
import { CHALLENGER as T } from '../tokens';

/**
 * Figma 707:10142 — Disposal details under Packing.
 * Header gap 4; options gap 24; question 16/20.
 */
const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${T.space[24]};
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Question = styled.h3`
  margin: 0;
  font-family: ${T.font.sans};
  font-weight: ${FontWeight.Medium};
  font-size: 16px;
  line-height: 20px;
  color: ${T.color.textPrimary};
`;

const HelpText = styled.p`
  margin: 0;
  font-family: ${T.font.sans};
  font-weight: ${FontWeight.Regular};
  font-size: 14px;
  line-height: 20px;
  color: ${T.color.textTertiary};
`;

type Props = {
  value?: boolean;
  onChange: (selected: boolean | undefined) => void;
};

export const DisposalSection: React.FC<Props> = ({ value, onChange }) => {
  const track = useTrack({ action: 'click' });
  const { disposalEligible } = useStorageCheckoutContext();
  const [showPricing, setShowPricing] = useState(false);

  const wasEligibleRef = useRef(disposalEligible);

  useEffect(() => {
    if (wasEligibleRef.current && !disposalEligible) {
      onChange(undefined);
    }
    wasEligibleRef.current = disposalEligible;
    // Match classic disposal: only re-run when eligibility flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disposalEligible]);

  useEffect(() => {
    if (!disposalEligible) return;
    track({
      action: 'display',
      metadata: {},
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- display once when eligibility becomes true
  }, [disposalEligible]);

  if (!disposalEligible) return null;

  return (
    <>
      <CardPadded>
        <Options>
          <Header>
            <Question>Do you need us to dispose of items?</Question>
            <HelpText>
              Have items you no longer want? Our team can pick them up for
              disposal when we take your items to storage.{' '}
              <InlineBrandLink
                type="button"
                onClick={() => {
                  setShowPricing(true);
                  track({
                    objectName: 'disposal_view_pricing',
                    label: 'View Pricing and Restrictions',
                    objectType: 'button',
                  });
                }}
              >
                Pricing and restrictions.
              </InlineBrandLink>
            </HelpText>
          </Header>
          <SelectStack>
            <SelectRow
              selected={value === false}
              onClick={() => {
                track({
                  objectName: 'disposal_add_on_card',
                  objectType: 'button',
                  label: 'Not now',
                  value: false,
                });
                onChange(false);
              }}
              title="No, I don’t need disposal right now"
              description="You can request disposal later through your Client portal."
            />
            <SelectRow
              selected={value === true}
              onClick={() => {
                track({
                  objectName: 'disposal_add_on_card',
                  objectType: 'button',
                  label: 'Yes, please!',
                  value: true,
                });
                onChange(true);
              }}
              title="Yes, I need disposal"
              description="You can tell us which items we will be disposing after booking your reservation."
              price="$50+"
            />
          </SelectStack>
        </Options>
      </CardPadded>
      <DisposalPricingModal
        isOpen={showPricing}
        onClose={() => setShowPricing(false)}
      />
    </>
  );
};
